/**
 * Dashboard — landing page for the Synapse editor suite.
 *
 * Lists every editor available in the monorepo as a card. Clicking a card
 * sets `activeView` in the editor store, which App.tsx watches to mount the
 * corresponding editor. The Back button inside each editor's LeftSidebar
 * routes back here.
 */

import React from 'react';
import { useEditorStore, type ActiveView } from './store/editorStore';
import { useAuthStore } from './store/authStore';
import ParticleWaveBackground from './components/dashboard/ParticleWaveBackground';
import { onWavePulse } from './components/dashboard/wavePulse';
import AdminUsersPanel from './components/editor/AdminUsersPanel';
import PerfStatsPanel from './components/editor/PerfStatsPanel';
import { IcUsers } from './components/ui/icons';
import { color, font } from './components/ui/theme';
import { useTheme, editorAccentHue } from './components/ui/themeStore';

interface EditorCard {
  /** When `status === 'ready'` this routes to a real editor view. Coming
   *  soon cards use a placeholder string and stay disabled. */
  view: Exclude<ActiveView, 'dashboard'> | string;
  title: string;
  status: 'ready' | 'coming-soon';
  accent: string;        // gradient base color
  accentSoft: string;    // soft border color
  emoji: string;
  /** If set, clicking opens this URL in a new tab instead of routing to an
   *  in-app editor view. */
  href?: string;
}

/** 2x2 grid laid out row-by-row:
 *     Synth VR        |  Synth Console
 *     Flow            |  Roobloops
 */
const EDITORS: EditorCard[] = [
  {
    view: 'synth-rider',
    title: 'Synth VR',
    status: 'ready',
    // Pink
    accent: '#FF2E88',
    accentSoft: 'rgba(255, 46, 136, 0.35)',
    emoji: '🎵',
  },
  {
    view: 'console',
    title: 'Synth Console',
    status: 'ready',
    // Blue
    accent: '#1CC5F0',
    accentSoft: 'rgba(28, 197, 240, 0.35)',
    emoji: '🎮',
  },
  {
    view: 'flow',
    title: 'Flow',
    status: 'ready',
    // Purple
    accent: '#A22BF0',
    accentSoft: 'rgba(162, 43, 240, 0.35)',
    emoji: '🌀',
  },
  {
    view: 'roobloops',
    title: 'Roobloops',
    status: 'ready',
    // Pure yellow (#ffd800) — the oklch theme ramp rendered it too orange/gold.
    accent: '#ffd800',
    accentSoft: 'rgba(255, 216, 0, 0.42)',
    emoji: '🧱',
    href: 'https://44.197.55.199/',
  },
];

export default function Dashboard() {
  const setActiveView = useEditorStore(s => s.setActiveView);
  const mapper = useAuthStore(s => s.mapper);
  const logout = useAuthStore(s => s.logout);
  const isAdmin = mapper?.role === 'admin';
  const setAdminUsersOpen = useEditorStore(s => s.setAdminUsersOpen);
  const [perfStatsOpen, setPerfStatsOpen] = React.useState(false);

  // Per-user app entitlements. Admins always see all cards; sessions without an
  // `apps` list (older tokens) also default to all so nobody gets locked out.
  // Restricted users see only cards whose `view` is in their allowed apps.
  const allowedApps = mapper?.apps;
  const visibleEditors = (isAdmin || !allowedApps)
    ? EDITORS
    : EDITORS.filter(ed => allowedApps.includes(ed.view));

  // The SYNAPSE outline lights up when drops collide in the wave. Each pulse
  // snaps the glow to full; it eases back off (CSS transition) when impacts
  // stop, so the outline "breathes" with wave activity.
  const [glow, setGlow] = React.useState(0);
  const glowOff = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    return onWavePulse(() => {
      setGlow(1);
      window.clearTimeout(glowOff.current);
      glowOff.current = window.setTimeout(() => setGlow(0), 650);
    });
  }, []);

  return (
    <div style={shellStyle}>
      <ParticleWaveBackground />
      <div style={topBarStyle}>
        {mapper && isAdmin && (
          <button
            type="button"
            onClick={() => setPerfStatsOpen(true)}
            style={settingsBtnStyle}
            title="Per-user framerate telemetry (admin)"
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(120, 80, 220, 0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(120, 80, 220, 0.25)'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            <span>Performance</span>
          </button>
        )}
        {mapper && isAdmin && (
          <button
            type="button"
            onClick={() => setAdminUsersOpen(true)}
            style={settingsBtnStyle}
            title="Manage users (admin)"
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(120, 80, 220, 0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(120, 80, 220, 0.25)'; }}
          >
            <IcUsers size={16} />
            <span>Users</span>
          </button>
        )}
        {mapper && (
          <button
            type="button"
            onClick={logout}
            style={settingsBtnStyle}
            title={`Sign out${mapper.display_name ? ` (${mapper.display_name})` : ''}`}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(120, 80, 220, 0.25)'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span>Logout</span>
          </button>
        )}
      </div>

      <div style={contentStyle}>
        <div style={headerStyle}>
          {/* Logo image turned off for now. Glass wordmark with an animated
              gradient outline (wave-like) replaces the plain title. */}
          <h1 className="synapse-wordmark dash-fade-in" style={{ ['--syn-glow' as any]: glow, animationDelay: '0.1s' }}>SYNAPSE</h1>
          <div className="dash-fade-in" style={{ ...subtitleStyle, animationDelay: '0.45s' }}>Beat Mapping Suite</div>
        </div>

        {/* The pills sit centred in the space BELOW the wordmark, landing in the
            lower half of the page. */}
        <div style={gridWrapStyle}>
          <div style={{ flex: 1.7 }} aria-hidden />
          <div className="dash-grid" style={gridStyle}>
            {visibleEditors.map((ed, i) => (
              <EditorCardComponent
                key={ed.view}
                card={ed}
                index={i}
                onClick={() => {
                  if (ed.status !== 'ready') return;
                  // External editors open in a new tab; in-app ones route via the store.
                  if (ed.href) {
                    window.open(ed.href, '_blank', 'noopener,noreferrer');
                    return;
                  }
                  setActiveView(ed.view as Exclude<ActiveView, 'dashboard'>);
                }}
              />
            ))}
          </div>
          <div style={{ flex: 1 }} aria-hidden />
        </div>

      </div>

      {/* Admin user-management modal (self-gates on adminUsersOpen + admin role). */}
      <AdminUsersPanel />
      {/* Admin performance-telemetry modal (self-gates on admin role). */}
      <PerfStatsPanel open={perfStatsOpen} onClose={() => setPerfStatsOpen(false)} />
    </div>
  );
}

// Each card slides in from its own outer corner (matches the 2×2 layout):
//   0 = top-left, 1 = top-right, 2 = bottom-left, 3 = bottom-right.
const CARD_CORNER = ['dash-card-tl', 'dash-card-tr', 'dash-card-bl', 'dash-card-br'];

function EditorCardComponent({ card, onClick, index }: { card: EditorCard; onClick: () => void; index: number }) {
  const ready = card.status === 'ready';
  // Each card shows ITS editor's own (user-chosen) accent colour.
  const slot = useTheme(s => s.editorAccents[card.view]);
  const hue = editorAccentHue(slot);
  // Roobloops is the exception: its pure yellow can't be reproduced by the fixed
  // 0.72-lightness oklch ramp (it comes out gold/orange), so it uses its exact
  // brand hex. Every other pill follows its per-editor theme accent.
  const fixedHex = card.view === 'roobloops';
  const accent = fixedHex ? card.accent : `oklch(0.72 0.20 ${hue})`;
  const accentSoft = fixedHex ? card.accentSoft : `oklch(0.72 0.20 ${hue} / 0.4)`;
  // Hover behaviour (fill pop-out + label colour flip + lift + glow) lives in CSS
  // (.dash-pill in index.css) so the spring/bounce easing is smooth; the per-pill
  // colours are fed in as CSS custom properties.
  return (
    <button
      type="button"
      onClick={ready ? onClick : undefined}
      disabled={!ready}
      className={`dash-pill ${CARD_CORNER[index] ?? 'dash-card-tl'}`}
      style={{
        ...cardStyle,
        cursor: ready ? 'pointer' : 'default',
        opacity: 1,
        // Vivid neon rim in the editor's accent.
        borderColor: accent,
        // Stagger the corner slide-ins so they cascade after the title fades.
        animationDelay: `${0.5 + index * 0.12}s`,
        ['--pill-accent' as any]: accent,
        // The label flips to the dark theme ink on hover for contrast on the
        // bright accent fill.
        ['--pill-ink' as any]: 'var(--bg-0)',
        ['--pill-rest-glow' as any]: `0 0 16px ${accentSoft}, 0 4px 16px rgba(0, 0, 0, 0.3)`,
        ['--pill-hover-glow' as any]: `0 0 30px ${accentSoft}, 0 10px 30px rgba(0, 0, 0, 0.35)`,
      }}
    >
      {/* Accent fill that springs out from the centre on hover. */}
      <span className="dash-pill__fill" aria-hidden />
      <div style={cardBodyStyle}>
        <div className="dash-pill__label" style={cardTitleStyle}>
          {card.title.toUpperCase()}
        </div>
      </div>
    </button>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const shellStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background:
    'radial-gradient(ellipse at top, rgba(91, 46, 197, 0.35) 0%, rgba(10, 5, 32, 1) 60%)',
  overflowY: 'auto',
  // The full-bleed wordmark spans 100vw; clip any horizontal spill so a vertical
  // scrollbar never triggers a sideways scroll.
  overflowX: 'hidden',
  fontFamily: font.sans,
  color: color.ink,
  // Column flex so the content can claim the full viewport height (via inset:0,
  // not 100vh) and centre the pills in the lower half.
  display: 'flex',
  flexDirection: 'column',
};

const contentStyle: React.CSSProperties = {
  position: 'relative',   // sit above the particle canvas (zIndex 0)
  zIndex: 1,
  width: '100%',
  maxWidth: 820,
  margin: '0 auto',
  padding: '52px 32px 40px',
  // Claim the shell's full height (which comes from inset:0, so this works even
  // where 100vh is unreliable) so the pills can centre in the lower half.
  flex: 1,
  minHeight: 0,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
};

// Eats the space below the wordmark and parks the pill grid LOW in it (biased
// downward via the 1.7 : 1 spacer split) so the pills sit centred in the lower
// half of the page rather than hugging the title.
const gridWrapStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
};

const headerStyle: React.CSSProperties = {
  textAlign: 'center',
  marginBottom: 56,
  // Break out of the centred 820px column so the fluid wordmark can fill the
  // whole screen width (clipped by the shell's overflowX:hidden).
  width: '100vw',
  marginLeft: '50%',
  transform: 'translateX(-50%)',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 'clamp(13px, 2vw, 26px)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.42em',
  color: 'rgba(255, 255, 255, 0.82)',
  // Pull the leading edge back by half a letter-spacing so the tracked text
  // stays visually centred under the wordmark.
  textIndent: '0.42em',
  marginTop: 'clamp(4px, 1vw, 16px)',
};

const gridStyle: React.CSSProperties = {
  // Locked 2x2: ready + coming-soon pair per row. auto-fit was rearranging
  // them on narrow viewports which broke the intended pairing.
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 18,
  width: '100%',
};

const cardStyle: React.CSSProperties = {
  position: 'relative',
  // Dark translucent fill so the wave shows through and the neon rim pops.
  background: 'rgba(12, 8, 28, 0.5)',
  // 2px rim — colour set per-card inline (the editor's accent).
  border: '2px solid rgba(120, 80, 220, 0.18)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  // Full pill.
  borderRadius: 999,
  overflow: 'hidden',
  textAlign: 'center',
  padding: 0,
  fontFamily: font.sans,
  color: 'inherit',
  transition: 'transform 0.22s ease, box-shadow 0.22s ease, border-color 0.2s ease',
  // box-shadow (rest + hover glow) is driven by .dash-pill in index.css via the
  // --pill-*-glow custom properties, so it's not set here.
};

const cardBodyStyle: React.CSSProperties = {
  // Pill — just the editor name, vertically centred. ~50% taller than the
  // original compact pill (60 → 90).
  padding: '27px 24px',
  minHeight: 90,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const cardTitleStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  textTransform: 'uppercase',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  // Colour + letter-spacing (incl. the hover spread) are handled by
  // .dash-pill__label in index.css so they animate with the fill pop.
};

const topBarStyle: React.CSSProperties = {
  position: 'absolute',
  top: 20,
  right: 20,
  zIndex: 2,
  display: 'flex',
  gap: 10,
};

const settingsBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 14px',
  background: 'rgba(15, 10, 35, 0.7)',
  border: '1px solid rgba(120, 80, 220, 0.25)',
  borderRadius: 8,
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  color: color.inkDim,
  fontFamily: font.sans,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'border-color 0.2s ease',
  zIndex: 10,
};
